import { useState, useEffect } from 'react';
import { useRequireAuth, useRequireRole } from '@/hooks/useRoleGuard';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Package, Users, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { useToast } from '@/hooks/use-toast';

interface MerchantProfile {
  id: string;
  name: string;
  website: string | null;
  status: string;
}

interface DashboardStats {
  totalListings: number;
  publishedListings: number;
  totalLeads: number;
  pendingLeads: number;
}

const MerchantDashboard = () => {
  const { user, isLoading } = useRequireAuth();
  useRequireRole('merchant');
  const { toast } = useToast();
  const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalListings: 0,
    publishedListings: 0,
    totalLeads: 0,
    pendingLeads: 0
  });
  const [loadingMerchant, setLoadingMerchant] = useState(true);

  useEffect(() => {
    if (!user) return;

    async function fetchMerchantData() {
      try {
        // Check if user has merchant profile
        const { data: merchantData, error: merchantError } = await supabase
          .from('merchants')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (merchantError && merchantError.code !== 'PGRST116') {
          console.error('Error fetching merchant:', merchantError);
          return;
        }

        if (merchantData) {
          setMerchant(merchantData);
          
          // Fetch dashboard stats
          const { data: listings } = await supabase
            .from('listings')
            .select('id, status')
            .eq('merchant_id', merchantData.id);

          const { data: leads } = await supabase
            .from('leads')
            .select('id, status, listing_id')
            .in('listing_id', listings?.map(l => l.id) || []);

          setStats({
            totalListings: listings?.length || 0,
            publishedListings: listings?.filter(l => l.status === 'published').length || 0,
            totalLeads: leads?.length || 0,
            pendingLeads: leads?.filter(l => l.status === 'new').length || 0
          });
        }
      } catch (error) {
        console.error('Error in fetchMerchantData:', error);
      } finally {
        setLoadingMerchant(false);
      }
    }

    fetchMerchantData();
  }, [user]);

  const createMerchantProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('merchants')
        .insert({
          user_id: user.id,
          name: user.email?.split('@')[0] || 'New Merchant',
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      setMerchant(data);
      toast({
        title: "Merchant profile created",
        description: "You can now start adding listings to your marketplace."
      });
    } catch (error: any) {
      console.error('Error creating merchant profile:', error);
      toast({
        title: "Error",
        description: "Failed to create merchant profile. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (isLoading || loadingMerchant) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Merchant Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Manage your listings and track your leads
          </p>
        </div>

        {!merchant ? (
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle>Welcome to Flexaura Marketplace</CardTitle>
              <CardDescription>
                Create your merchant profile to start selling with Buy Now, Pay Later options
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={createMerchantProfile} className="w-full">
                Create Merchant Profile
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Merchant Status */}
            <Card>
              <CardHeader>
                <CardTitle>Profile Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{merchant.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Status: <span className={`capitalize ${
                        merchant.status === 'approved' ? 'text-green-600' : 
                        merchant.status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {merchant.status}
                      </span>
                    </p>
                  </div>
                  <Link to="/merchant/profile">
                    <Button variant="outline">Edit Profile</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-2xl font-bold">{stats.totalListings}</p>
                      <p className="text-xs text-muted-foreground">Total Listings</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-2xl font-bold">{stats.publishedListings}</p>
                      <p className="text-xs text-muted-foreground">Published</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-2xl font-bold">{stats.totalLeads}</p>
                      <p className="text-xs text-muted-foreground">Total Leads</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-yellow-600" />
                    <div>
                      <p className="text-2xl font-bold">{stats.pendingLeads}</p>
                      <p className="text-xs text-muted-foreground">New Leads</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Listings</CardTitle>
                  <CardDescription>Manage your product and service listings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Link to="/merchant/listings">
                    <Button variant="outline" className="w-full justify-start">
                      <Package className="mr-2 h-4 w-4" />
                      View All Listings
                    </Button>
                  </Link>
                  <Link to="/merchant/listings/new">
                    <Button className="w-full justify-start">
                      <Plus className="mr-2 h-4 w-4" />
                      Create New Listing
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Leads</CardTitle>
                  <CardDescription>Track and manage customer inquiries</CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to="/merchant/leads">
                    <Button variant="outline" className="w-full justify-start">
                      <Users className="mr-2 h-4 w-4" />
                      View All Leads
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantDashboard;